import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import * as styles from './RoomIndex.module.scss'

import { PATH } from '../../../constants'
import {
  useCreateNextPath,
  useNearbyHotelsApi,
  usePropertyProvider,
  useRoomsApi,
  useSearchQueryParams,
} from '../../../hooks'
import { Sort } from '../../basics'
import {
  Loader,
  NotFound,
  ShowAllToggle,
  SoldOutRecommend,
} from '../../contents'
import { RoomItem } from '../RoomItem'

const translateNamespace = 'components.RoomIndex'

// FIXME style
export const RoomIndex: React.FC = () => {
  const { isHotel } = usePropertyProvider()
  const {
    params: { property },
    onCreateNextPath,
  } = useCreateNextPath()
  const { params: queryParams } = useSearchQueryParams()
  const {
    data: rooms,
    isLoading,
    error,
  } = useRoomsApi({
    property,
    ...queryParams,
    showAllRooms: queryParams.showAll,
    roomSlug: queryParams.room,
  })
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Only trust the rooms payload when the request succeeded. On error SWR
  // returns `data` as undefined, which must not be read as "sold out".
  const loadedRooms = !error && Array.isArray(rooms) ? rooms : undefined

  // A room is reservable only when it can be booked at a sellable price.
  // minPrice <= 0 means "stop selling" (mirrors RoomItem's isAvailable).
  const hasReservableRoom = !!loadedRooms?.some(
    (room) => room.canReserve && room.minPrice > 0
  )

  // Sold-out recommendation is shown for every hotel facility (not just T11).
  // The rooms list defaults to showAll, so a sold-out response can still return
  // non-reservable room categories — treat as sold out only on a successful
  // response with no reservable room, never on an API error. Non-hotel
  // facilities (activities/spa) keep the legacy NotFound, since a "nearby
  // hotels + cross-search" recommendation is out of context for them.
  //
  // The "予約可能なもののみ表示" toggle being OFF (showAll=true) is an explicit
  // "show me everything" request: keep listing all room categories (sold-out
  // ones render as 販売なし) instead of swapping in the sold-out recommendation,
  // so the user can still browse the full lineup on a fully-booked date. The
  // recommendation only kicks in when the toggle is ON (showAll=false) and there
  // is genuinely nothing reservable.
  //
  // A `?room=` filter scopes `useRoomsApi` to a single room slug, so an empty
  // reservable set then means "that one room category is unavailable", NOT that
  // the whole hotel is sold out. Skip the hotel-wide recommendation in that case
  // and fall back to NotFound — otherwise we'd hide the other available rooms.
  const isSoldOut =
    isHotel &&
    !queryParams.showAll &&
    !queryParams.room &&
    loadedRooms !== undefined &&
    !hasReservableRoom

  // Warm up the nearby-hotels query as soon as we know the facility is sold out,
  // so SoldOutRecommend's <NearbyHotels> hits a primed (or in-flight) SWR cache
  // instead of starting the slow cross-hotel sweep only after it mounts. Same
  // SWR key as NearbyHotels, so the two requests are deduped. `enabled` gates it
  // off entirely when not sold out (and hooks must run unconditionally, so this
  // call stays above the early returns below).
  useNearbyHotelsApi({
    checkInDate: queryParams.checkInDate,
    checkOutDate: queryParams.checkOutDate,
    roomCount: queryParams.roomCount,
    numberOfAdults: queryParams.numberOfAdults,
    numberOfChildren: queryParams.numberOfChildren,
    numberOfInfants: queryParams.numberOfInfants,
    limit: 10,
    enabled: isSoldOut,
  })

  if (isLoading) {
    return <Loader />
  }

  const i18nKey = isHotel ? 'empty.roomNotFound' : 'empty.facilityNotFound'

  const emptyContent = isSoldOut ? (
    <SoldOutRecommend />
  ) : (
    <NotFound
      title={t(`${i18nKey}.attention`)}
      description1={t(`${i18nKey}.direction`)}
      description2={t(`${i18nKey}.news`)}
    />
  )

  return (
    <React.Fragment>
      <Sort
        // When sold out the room list is replaced by SoldOutRecommend, so the
        // result count must read 0 rather than the raw (non-reservable) length.
        count={isSoldOut ? 0 : rooms?.length ?? 0}
        onChange={(e) => {
          const nextPath = onCreateNextPath(PATH.rooms, {
            queryParams: { roomOrder: e.target.value },
          })
          navigate(nextPath)
        }}
        defaultValue={queryParams.roomOrder ?? 'min_price'}
        noMargin
      >
        <option value="min_price">
          {t(`${translateNamespace}.Sort.ascPrice`)}
        </option>
        <option value="-min_price">
          {t(`${translateNamespace}.Sort.descPrice`)}
        </option>
      </Sort>
      <ShowAllToggle />
      <ul>
        {!isSoldOut && rooms && rooms.length > 0
          ? rooms.map((room) => (
              <li className={styles.item} key={room.id}>
                <RoomItem {...room} />
              </li>
            ))
          : emptyContent}
      </ul>
    </React.Fragment>
  )
}
